// Where a session came from, and how far into it a moment happened (D250,
// `KOLONY-Acquisition-Telemetry-Analysis.md` A1–A3).
//
// Pure, and deliberately separate from the emitter: these four decisions are the ones that
// can be WRONG in a way no dashboard would reveal — a referrer that leaks a path, a
// campaign tag that becomes free text, a timing that becomes a fingerprint, a share id
// derived from a room code. Each is one function with its own spec.

/** What a value looks like when there is nothing to report: absent, never a placeholder. */
const NONE = null;

/**
 * The registrable-ish host of `document.referrer`, or null.
 *
 * HOST ONLY, and that is the whole point: a full referrer carries the previous page's path
 * and query, which is somebody else's data and is exactly how analytics stacks end up
 * holding search terms and session tokens they never meant to collect.
 *
 * A SAME-HOST referrer is null too — that is internal navigation, not a referral, and
 * counting it would make our own pages look like our best acquisition channel.
 */
export function referrerHost(raw: string | null | undefined, selfHost: string): string | null {
  if (!raw) return NONE;
  let host: string;
  try {
    host = new URL(raw).hostname.toLowerCase();
  } catch {
    return NONE; // an unparseable referrer is not a channel
  }
  if (!host) return NONE;
  const bare = host.startsWith('www.') ? host.slice(4) : host;
  const self = selfHost.toLowerCase().replace(/^www\./, '');
  return bare === self || bare.length === 0 ? NONE : bare.slice(0, 64);
}

/** `[a-z0-9._-]`, lower-cased, trimmed to `max`. Anything else is dropped — a campaign tag
 *  is ours by construction, so it is never free text and never has to be escaped later. */
function cleanTag(raw: string | null, max: number): string | null {
  if (!raw) return NONE;
  const cleaned = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '');
  return cleaned.length === 0 ? NONE : cleaned.slice(0, max);
}

/**
 * `utm_source` (+ `/utm_campaign` when both are present), or null.
 *
 * Only these two of the five UTM parameters: medium, term and content describe an ad
 * buy we do not make, and a field nobody reads is a field that only carries risk.
 */
export function campaignTag(search: string): string | null {
  const q = new URLSearchParams(search);
  const source = cleanTag(q.get('utm_source'), 24);
  const campaign = cleanTag(q.get('utm_campaign'), 24);
  if (source === null && campaign === null) return NONE;
  if (source === null) return campaign;
  return campaign === null ? source : `${source}/${campaign}`;
}

/** The buckets a first-session timing is reported in. Ordered, and they are the labels. */
export const TIME_BUCKETS = ['<5s', '5-15s', '15-60s', '1-5m', '>5m'] as const;
export type TimeBucket = (typeof TIME_BUCKETS)[number];

/**
 * Milliseconds since page load → a bucket.
 *
 * BUCKETED, never raw: a set of millisecond timings is an entropy source, and no decision
 * this data serves needs more resolution than "did they get into a game in the first
 * minute".
 *
 * Only NaN is special-cased, and it lands in the FIRST bucket. Infinity is left to fall
 * through the ordinary comparisons into the last one, because the alternative — one guard
 * sending every non-finite value to `<5s` — would let a broken clock inflate the "arrived
 * and was playing within five seconds" bucket, which is the single number this whole
 * measurement exists to watch.
 */
export function timeBucket(ms: number): TimeBucket {
  if (Number.isNaN(ms) || ms < 5_000) return '<5s';
  if (ms < 15_000) return '5-15s';
  if (ms < 60_000) return '15-60s';
  if (ms < 300_000) return '1-5m';
  return '>5m';
}

const SHARE_ID_LEN = 8;
const SHARE_ID_RE = /^[a-z0-9]{8}$/;

/**
 * A fresh opaque share id.
 *
 * RANDOM, not derived from the room code — the analysis proposed a salted hash of the
 * code and random is strictly better for the same measurement: a hash keeps a relationship
 * to a live capability (and the salt would ship in the bundle, so it protects nothing),
 * while a random token is related to nothing at all. Both ends report the same id, which
 * is all the loop needs.
 */
export function newShareId(bytes: (n: number) => Uint8Array = cryptoBytes): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const raw = bytes(SHARE_ID_LEN);
  let out = '';
  for (let i = 0; i < SHARE_ID_LEN; i++) out += alphabet[(raw[i] ?? 0) % alphabet.length];
  return out;
}

function cryptoBytes(n: number): Uint8Array {
  const buf = new Uint8Array(n);
  globalThis.crypto?.getRandomValues?.(buf);
  return buf;
}

/**
 * The share id on an arriving URL, or null.
 *
 * VALIDATED against the shape we mint, because this value is copied off a link a stranger
 * can edit and is about to be written into an events table: anything that is not one of
 * our own ids is not a share we can attribute, and echoing it would be storing arbitrary
 * text handed to us by whoever wrote the link.
 */
export function shareIdFrom(search: string): string | null {
  const raw = new URLSearchParams(search).get('s');
  return raw !== null && SHARE_ID_RE.test(raw) ? raw : NONE;
}
